// * We define calculator types as close to an enum as we can get in JS
const CalculatorType = Object.freeze({
    SIMPLE: "SIMPLE", // The calculator is a simple one, that only requires the amount
    AREA: "AREA", // The calculator is an area calculator, that requires an area (m2) to calculate the amount
    LENGTH: "LENGTH", // The calculator is a length calculator, that requires a length (m) to calculate the amount
    PROFILE: "PROFILE", // The calculator is a profile calculator, that calculates the total length based on the amount
    EGALINE: "EGALINE", // The calculator is an egeline calculator, that calculates the package amount area and layer thickness
});

class Calculator {
    /**
     * Create a new calculator
     * @param {CalculatorType} type The type of calculator to create (SIMPLE, AREA, LENGTH)
     * @param {Object} productData The data of the product, this should be retrieved in Liquid and passed to this class
     */
    constructor(type, productData) {
        // * Validate if the required parameters are passed and are of the correct type
        if (!Object.values(CalculatorType).includes(type)) {
            throw new Error("Invalid calculator type");
        }
        if (typeof productData !== "object" || productData === null) {
            throw new Error("Invalid product data");
        }

        // * Set the type and product data
        this.type = type;
        this.productData = productData;

        // * Get the variant select wrapper
        const variantSelectWrapper = document.querySelector(".variant-selection");
        if (variantSelectWrapper) {
            this.variantSelectWrapper = variantSelectWrapper;
        }

        // * Store price total display
        this.priceTotalDisplay = document.querySelector("#product-total-price");

        // * Setup class properties
        this.inputs = this.#setupInputs();
        this.baseValues = this.#setupBaseValues();

        // * Setup event listeners for the inputs
        this.#setupListeners();
    }

    // ==============================
    // SETUP METHODS
    // ==============================

    /**
     * Gets all inputs needed for the given calculator type
     * @returns {Object} An object with the inputs needed for the calculator
     * @throws {Error} If not all inputs are found for the given calculator type, causing the calculator to not work
     */
    #setupInputs() {
        switch (this.type) {
            case CalculatorType.SIMPLE: {
                const amountInput = document.querySelector("#product-quantity-input");
                if (amountInput == undefined) {
                    throw new Error("Not all inputs found for creating a simple calculator");
                }
                return {
                    amount: amountInput,
                };
            }
            case CalculatorType.AREA: {
                const areaInput = document.querySelector("#areaInput");
                const amountInput = document.querySelector("#product-quantity-input");
                if (areaInput == undefined || amountInput == undefined) {
                    throw new Error("Not all inputs found for creating an area calculator");
                }
                return {
                    area: areaInput,
                    amount: amountInput,
                };
            }
            case CalculatorType.LENGTH: {
                const lengthInput = document.querySelector("#lengthInput");
                const amountInput = document.querySelector("#product-quantity-input");
                if (lengthInput == undefined || amountInput == undefined) {
                    throw new Error("Not all inputs found for creating a length calculator");
                }
                return {
                    length: lengthInput,
                    amount: amountInput,
                };
            }
            case CalculatorType.PROFILE: {
                const amountInput = document.querySelector("#product-quantity-input");
                const profileText = document.querySelector("#profileText");

                if (amountInput == undefined || profileText == undefined) {
                    throw new Error("Not all inputs found for creating a profile calculator");
                }

                return {
                    amount: amountInput,
                    profile: profileText,
                };
            }
            case CalculatorType.EGALINE: {
                const amountInput = document.querySelector("#product-quantity-input");
                const areaInput = document.querySelector("#areaInput");
                const thicknessInput = document.querySelector("#thicknessInput");
                const packageWeight = document.querySelector("#profileText");

                if (amountInput == undefined || areaInput == undefined || thicknessInput == undefined) {
                    throw new Error("Not all inputs found for creating an egeline calculator");
                }

                return {
                    amount: amountInput,
                    area: areaInput,
                    thickness: thicknessInput,
                    packageWeight: packageWeight,
                };
            }
        }
    }

    /**
     * Creates an object with the base values that the calculator needs
     * @returns {Object} An object with the base values needed for the calculator
     */
    #setupBaseValues() {
        let baseValues = {
            price: document.querySelector("#calculator").getAttribute("data-pr-up"),
            compareAtPrice: document.querySelector("#product-compare-price").getAttribute("data-price"),
            contents: parseFloat(document.querySelector("#calculator").getAttribute("data-contents")) ?? 1,
        };

        // * Set additional the base values based on the calculator type
        if (this.type === CalculatorType.AREA) {
            baseValues.cuttingLoss = 0;
        }

        if (this.type === CalculatorType.EGALINE) {
            let usage = parseFloat(document.querySelector("#calculator").getAttribute("data-usage"));
            baseValues.usage = isNaN(usage) ? 1 : usage;

            let packageWeight = document.querySelector("#calculator").getAttribute("data-weight");
            packageWeight = packageWeight.replace(/\D/g, "");
            baseValues.packageWeight = parseFloat(packageWeight);
        }

        // ? You can set additional base values here based on the calculator type by adding if-statements
        // ? and then set the key on the baseValues object like this:
        // baseValues.additionalValue = 'your-addional-value';

        return baseValues;
    }

    /**
     * Sets the base values for the calculator for the current variant based on the current url
     * @returns {void}
     */
    #setVariantBaseValues() {
        // Get the selected variant from the url
        const variantId = new URL(window.location.href).searchParams.get("variant");

        if (variantId == undefined || variantId == null) return;

        // Find the variant in the product data
        const variant = this.productData.variants.find((variant) => variant.id == variantId);
        const variantMeta = this.productData.variant_metafields.find((meta) => meta.id == variantId);

        switch (this.type) {
            case CalculatorType.SIMPLE: {
                this.baseValues.price = variant.price;
                this.baseValues.compareAtPrice = variant.compare_at_price;

                // * We update the calculator here to set the values based on the selected variant
                this.calculate(this.inputs.amount); // Use the amount input to trigger the re-calculation of the other fields
                break;
            }
            case CalculatorType.LENGTH:
            case CalculatorType.AREA:
            case CalculatorType.PROFILE: {
                this.baseValues.price = variant.price;
                this.baseValues.compareAtPrice = variant.compare_at_price;
                this.baseValues.contents = variantMeta.metafields.amount ?? this.baseValues.contents;

                // * We update the calculator here to set the values based on the selected variant
                this.calculate(this.inputs.amount); // Use the amount input to trigger the re-calculation of the other fields
                break;
            }
        }
    }

    /**
     * Sets up the event listeners and functionality of the cutting loss checkboxes for the area calculator
     * @returns {void}
     */
    #setupCuttingLoss() {
        const cuttingLossCheckboxes = document.querySelectorAll(".loss-checkbox");
        if (cuttingLossCheckboxes == undefined) return;
        for (const checkbox of cuttingLossCheckboxes) {
            checkbox.addEventListener("change", () => {
                // Uncheck all other checkboxes
                cuttingLossCheckboxes.forEach((cb) => (cb !== checkbox ? (cb.checked = false) : null));

                // Update cutting loss value and recalculate
                this.baseValues.cuttingLoss = checkbox.checked ? parseFloat(checkbox.value) : 0;
                this.calculate(this.inputs.area);
            });
        }
    }

    // ==============================
    // CLASS SPECIFIC HELPER METHODS
    // ==============================

    /**
     * Validate that the input value is a positive decimal number
     * @param {HTMLElementEventMap} input The input to validate
     * @returns {bool} Returns true if the input is valid, false if it is not
     */
    #validateInput(input) {
        const value = parseFloat(input.value);
        if (input === this.inputs.amount) {
            //* if the input is the amount input, we only want to allow positive integers
            if (!Number.isInteger(value) || value <= 0) {
                input.value = "";
                return false;
            }
        } else if (isNaN(value) || value <= 0) {
            //* if the input is the area input, we want to allow positive decimal numbers
            input.value = input.value.replace(".", ",");

            this.value = this.value.replace(/[^0-9,]+/g, "");

            var t = input.value;
            if (input.value.includes(",")) {
                input.value = t.indexOf(",") >= 0 ? t.substr(0, t.indexOf(",")) + t.substr(t.indexOf(","), 3) : t;
            } else if (input.value.includes(".")) {
                input.value = t.indexOf(".") >= 0 ? t.substr(0, t.indexOf(".")) + t.substr(t.indexOf("."), 3) : t;
            }

            return false;
        } else if (value > 10000) {
            input.value = "10000";
            return false;
        }

        return true;
    }

    // ==============================
    // EVENT LISTENERS
    // ==============================

    /**
     * Setup event listeners for the inputs
     * @returns {void}
     */
    #setupListeners() {
        // * Add event listeners for calculator inputs
        for (const input of Object.values(this.inputs)) {
            input.addEventListener("input", () => {
                const valid = this.#validateInput(input);

                if (!valid) return;
                this.priceTotalDisplay.classList.remove("hidden");
                this.calculate(input);
            });
        }

        // * Add event listener for changing the selected variant
        document.querySelectorAll(".options-selection__option-value-input, .options-selection__input-select").forEach((element) => {
            element.addEventListener("change", (_) => {
                // Workaround for Firefox as it triggers the change event before the variant url is actually changed due to race conditions
                setTimeout(() => {
                    this.#setVariantBaseValues();
                    this.updateUnitPrice();
                }, 1);
            });
        });

        // * Add event listeners for the '+' and '-' buttons
        const extraButtons = document.querySelectorAll(".input-extra-button");
        extraButtons.forEach((button) => {
            button.addEventListener("click", (_) => {
                this.priceTotalDisplay.classList.remove("hidden");
                this.changeQty(button);
            });
        });

        // * Add event listener for changing the cutting loss checkboxes
        if (this.type === CalculatorType.AREA) {
            this.#setupCuttingLoss();
        }
    }

    // ==============================
    // METHODS
    // ==============================

    // ? Not 100% sure if this function should be here or in a seperate place as it technically doesn't belong to the calculator,
    // ? but as we have all the needed data here, this is for now the easiest place to put it
    /**
     * Calculates and sets the unit price on the product page based on the current base values
     * @returns {void}
     */
    updateUnitPrice() {
        const unitPrice = Math.round(this.baseValues.price / this.baseValues.contents);
        const formattedPrice = formatPrice(unitPrice, "nl-NL", "EUR");

        if (this.productData.variants.length <= 1 || (this.productData.variants.length > 1 && this.variantSelectWrapper && this.variantSelectWrapper.getAttribute("variant") !== "not-selected")) {
            document.querySelector("#price-per-unit").innerText = formattedPrice;
        }
    }

    /**
     * Change the quantity of the product based on the input
     * @param {HTMLElement} button The button that was clicked
     * @returns {void}
     */
    changeQty(button) {
        if (button.getAttribute("data-type") === "decrease") {
            if (this.inputs.amount.value > 1) {
                this.inputs.amount.value = parseInt(this.inputs.amount.value) - 1;
            }
        } else if (button.getAttribute("data-type") === "increase") {
            this.inputs.amount.value = parseInt(this.inputs.amount.value) + 1;
        }

        // Recalculate the values
        this.calculate(this.inputs.amount);
    }

    /**
     * Calculate the amount of the product based on the inputs
     * @param {HTMLElement} changedInput The input that was changed
     * @returns {void}
     */
    calculate(changedInput) {
        switch (this.type) {
            // * SIMPLE (Stuk)
            case CalculatorType.SIMPLE: {
                // Calculate and set the price based on the amount
                const minPriceDiv = document.querySelector(".price__current span.money.price__current--min");
                if (minPriceDiv) {
                    minPriceDiv.innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                }
                document.querySelector("#product-price").innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                document.querySelector("#product-compare-price").innerText = formatPrice(this.baseValues.compareAtPrice * this.inputs.amount.value, "nl-NL", "EUR");

                break;
            }
            // * AREA (m2)
            case CalculatorType.AREA: {
                if (changedInput === this.inputs.area) {
                    // Calculate the amount of packages based on the area
                    const cuttingLossExtra = this.inputs.area.value * (this.baseValues.cuttingLoss / 100);
                    const amount = Math.ceil((parseFloat(this.inputs.area.value) + cuttingLossExtra) / this.baseValues.contents);
                    this.inputs.amount.value = amount;
                } else {
                    // Calculate the area based on the amount of packages
                    const area = this.inputs.amount.value * this.baseValues.contents;
                    const cuttingLoss = area * (this.baseValues.cuttingLoss / 100);
                    this.inputs.area.value = (area - cuttingLoss).toFixed(2);
                }

                // Calculate and set the price based on the amount
                document.querySelector("#product-price").innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                document.querySelector("#product-compare-price").innerText = formatPrice(this.baseValues.compareAtPrice * this.inputs.amount.value, "nl-NL", "EUR");

                break;
            }
            // * LENGTH (meter)
            case CalculatorType.LENGTH: {
                if (changedInput === this.inputs.length) {
                    // Calculate the amount of packages based on the length
                    const amount = Math.ceil(this.inputs.length.value / this.baseValues.contents);
                    this.inputs.amount.value = amount;
                } else {
                    // Calculate the length based on the amount of packages
                    const length = this.inputs.amount.value * this.baseValues.contents;
                    this.inputs.length.value = length.toFixed(2);
                }

                // Calculate and set the price based on the amount
                const minPriceDiv = document.querySelector(".price__current span.money.price__current--min");
                if (minPriceDiv) {
                    minPriceDiv.innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                }
                document.querySelector("#product-price").innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                document.querySelector("#product-compare-price").innerText = formatPrice(this.baseValues.compareAtPrice * this.inputs.amount.value, "nl-NL", "EUR");

                break;
            }
            // * PROFILE (profielen)
            case CalculatorType.PROFILE: {
                const length = this.inputs.amount.value * this.baseValues.contents;
                this.inputs.profile.innerText = length.toFixed(2);

                // Calculate and set the price based on the amount
                const minPriceDiv = document.querySelector(".price__current span.money.price__current--min");
                if (minPriceDiv) {
                    minPriceDiv.innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                }
                document.querySelector("#product-price").innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                document.querySelector("#product-compare-price").innerText = formatPrice(this.baseValues.compareAtPrice * this.inputs.amount.value, "nl-NL", "EUR");

                break;
            }
            // * EGALINE
            case CalculatorType.EGALINE: {
                if (changedInput === this.inputs.area || changedInput === this.inputs.thickness) {
                    // Calculate the amount of packages based on the area and thickness
                    const amount = Math.ceil((this.inputs.area.value * this.inputs.thickness.value * this.baseValues.usage) / this.baseValues.packageWeight);

                    this.inputs.amount.value = amount;
                } else {
                    // Calculate the area and thickness based on the amount of packages, set the thickness to 2 and calculate the area
                    this.inputs.thickness.value = 2;
                    const area = (this.inputs.amount.value * this.baseValues.packageWeight) / (this.baseValues.usage * this.inputs.thickness.value);

                    this.inputs.area.value = area.toFixed(2);
                }

                const weight = this.inputs.area.value * this.inputs.thickness.value * this.baseValues.usage;
                this.inputs.packageWeight.innerText = weight.toFixed(2);

                // Calculate and set the price based on the amount
                const minPriceDiv = document.querySelector(".price__current span.money.price__current--min");
                if (minPriceDiv) {
                    minPriceDiv.innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                }
                document.querySelector("#product-price").innerText = formatPrice(this.baseValues.price * this.inputs.amount.value, "nl-NL", "EUR");
                document.querySelector("#product-compare-price").innerText = formatPrice(this.baseValues.compareAtPrice * this.inputs.amount.value, "nl-NL", "EUR");

                break;
            }
        }
    }
}
